<template>
  <form-section
    v-if="isEmailSettingsVisible"
    :title="$t('ProjectSettings_Row_Features')"
  >
    <el-form :model="value" :disabled="isEmailSettingsDisabled">
      <el-row>
        <el-col :span="12">
          <form-item
            prop="push_encryption"
            :label="$t('ProjectSettings_Field_Encryption')"
            :description="$t('ProjectSettings_Field_Encryption_Desc')"
          >
            <feature-toggle v-model="value.push_encryption" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="push_delivery_receipts"
            :label="$t('ProjectSettings_Field_PushDeliveryReceipts')"
            :description="$t('ProjectSettings_Field_PushDeliveryReceipts_Desc')"
          >
            <feature-toggle v-model="value.push_delivery_receipts" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="segmentation_events_use_profile"
            :label="$t('ProjectSettings_Field_SegmentationEventsUseProfile')"
            :description="
              $t('ProjectSettings_Field_SegmentationEventsUseProfile_Desc')
            "
          >
            <feature-toggle v-model="value.segmentation_events_use_profile" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <div class="flexbox">
            <form-item
              prop="maxautomation"
              :label="$t('ProjectSettings_Field_Workflow')"
              :description="
                !features.maxautomation && features.maxautomation_disabled
                  ? $t('ProjectSettings_Field_Workflow_Desc_Disabled')
                  : $t('ProjectSettings_Field_Workflow_Desc')
              "
            >
              <feature-toggle
                v-model="value.maxautomation"
                :disabled="
                  !features.maxautomation && features.maxautomation_disabled
                "
              />
            </form-item>
            <form-item class="p-left-80" v-if="checkAuditLogLink">
              <el-link
                :href="workflowActivityAuditLink"
                target="_blank"
                :underline="false"
              >
                <el-button type="primary" plain icon="el-icon-notebook-2">
                  {{ $t('ProjectSettings_AuditLog') }}
                </el-button>
              </el-link>
            </form-item>
          </div>
        </el-col>
        <template v-if="value.email">
          <el-col :span="12">
            <form-item
              prop="email_attachments"
              :label="$t('ProjectSettings_Field_EmailAttachments')"
              :description="$t('ProjectSettings_Field_EmailAttachments_Desc')"
            >
              <feature-toggle v-model="value.email_attachments" />
            </form-item>
          </el-col>
          <el-col :span="12">
            <form-item
              prop="profile_idfyby_email"
              :label="$t('ProjectSettings_Field_ProfileIdentifyByEmail')"
              :description="
                $t('ProjectSettings_Field_ProfileIdentifyByEmail_Desc')
              "
            >
              <feature-toggle v-model="value.profile_idfyby_email" />
            </form-item>
          </el-col>
          <el-col :span="12">
            <form-item
              prop="profile_email_unique"
              :label="$t('ProjectSettings_Field_ProfileEmailUnique')"
              :description="$t('ProjectSettings_Field_ProfileEmailUnique_Desc')"
            >
              <el-tooltip
                effect="light"
                :disabled="!value.gig_integration"
                :content="$t('ProjectSettings_Tooltip_Profile_Email_Unique')"
                placement="right"
              >
                <feature-toggle
                  :disabled="!!value.gig_integration"
                  v-model="value.profile_email_unique"
                />
              </el-tooltip>
            </form-item>
          </el-col>
        </template>
        <el-col :span="12" v-if="$hasFeature('sportradarGlobalFlag')">
          <form-item
            prop="sportradar"
            :label="$t('ProjectSettings_Field_Sportradar')"
            :description="$t('ProjectSettings_Field_Sportradar_Desc')"
          >
            <feature-toggle v-model="value.sportradar" />
            <small class="col-12" v-if="shouldShowSportradarWarning">
              <br />
              <i class="el-icon-warning-outline"></i>
              {{ $t('ProjectSettings_Field_Sportradar_Warning') }}
            </small>
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="content_import_export"
            :label="$t('ProjectSettings_Field_ContentImportExport')"
            :description="$t('ProjectSettings_Field_ContentImportExport_Desc')"
          >
            <feature-toggle v-model="value.content_import_export" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="subscription_tracking"
            :label="$t('ProjectSettings_Field_SubscriptionTracking')"
            :description="$t('ProjectSettings_Field_SubscriptionTracking_Desc')"
          >
            <feature-toggle v-model="value.subscription_tracking" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="subscription_preferences"
            :label="$t('ProjectSettings_Field_SubscriptionPreferences')"
            :description="
              $t('ProjectSettings_Field_SubscriptionPreferences_Desc')
            "
          >
            <el-tooltip
              effect="light"
              :disabled="!value.igt_integration"
              :content="
                $t('ProjectSettings_Tooltip_Subscription_Preferences_IGT')
              "
              placement="right"
            >
              <feature-toggle
                :disabled="!!value.igt_integration"
                v-model="value.subscription_preferences"
              />
            </el-tooltip>
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="meta"
            :label="$t('ProjectSettings_Field_Meta')"
            :description="$t('ProjectSettings_Field_Meta_Desc')"
          >
            <feature-toggle v-model="value.meta" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="googleads"
            :label="$t('ProjectSettings_Field_Googleads')"
            :description="$t('ProjectSettings_Field_Googleads_Desc')"
          >
            <feature-toggle v-model="value.googleads" />
          </form-item>
        </el-col>
        <el-col :span="12" v-if="$hasFeature('micrositesGlobalFlag')">
          <form-item
            prop="microsites"
            :label="$t('ProjectSettings_Field_Microsites')"
            :description="$t('ProjectSettings_Field_Microsites_Desc')"
          >
            <feature-toggle v-model="value.microsites" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="campaign_engagement_delta_report"
            :label="$t('ProjectSettings_Field_CampaignEngagementDeltaReport')"
            :description="
              $t('ProjectSettings_Field_CampaignEngagementDeltaReport_Desc')
            "
          >
            <feature-toggle v-model="value.campaign_engagement_delta_report" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="gig_integration"
            :label="$t('ProjectSettings_Field_Gig_Integration')"
            :description="$t('ProjectSettings_Field_Gig_Integration_Desc')"
          >
            <el-tooltip
              effect="light"
              :disabled="!!value.profile_email_unique"
              :content="$t('ProjectSettings_Tooltip_GiG')"
              placement="right"
            >
              <feature-toggle
                :disabled="!value.profile_email_unique"
                v-model="value.gig_integration"
              />
            </el-tooltip>
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="show_lithium_integration"
            :label="$t('ProjectSettings_Field_ShowLithiumIntegration')"
            :description="
              $t('ProjectSettings_Field_ShowLithiumIntegration_Desc')
            "
          >
            <feature-toggle v-model="value.show_lithium_integration" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="show_lithium_v2_integration"
            :label="$t('ProjectSettings_Field_ShowLithiumV2Integration')"
            :description="
              $t('ProjectSettings_Field_ShowLithiumV2Integration_Desc')
            "
          >
            <feature-toggle v-model="value.show_lithium_v2_integration" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="audit_log_profile"
            :label="$t('ProjectSettings_Field_AuditLog_Profile')"
            :description="$t('ProjectSettings_Field_AuditLog_Profile_Desc')"
          >
            <feature-toggle v-model="value.audit_log_profile" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="audit_log_campaign"
            :label="$t('ProjectSettings_Field_AuditLog_Campaign')"
            :description="$t('ProjectSettings_Field_AuditLog_Campaign_Desc')"
          >
            <feature-toggle v-model="value.audit_log_campaign" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="feature_seed_list"
            :label="$t('ProjectSettings_Field_Feature_SeedList')"
            :description="$t('ProjectSettings_Field_Feature_SeedList_Desc')"
          >
            <feature-toggle v-model="value.feature_seed_list" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="disable_event_triggered_campaign_timeseries_segment"
            :label="
              $t('ProjectSettings_Field_Event_Triggered_Timeseries_Segments')
            "
            :description="
              $t(
                'ProjectSettings_Field_Event_Triggered_Timeseries_Segments_Desc'
              )
            "
          >
            <feature-toggle
              v-model="
                value.disable_event_triggered_campaign_timeseries_segment
              "
            />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="disable_mobile_validation"
            :label="$t('ProjectSettings_Field_Disable_Mobile_Validation')"
            :description="
              $t('ProjectSettings_Field_Disable_Mobile_Validation_Desc')
            "
          >
            <feature-toggle v-model="value.disable_mobile_validation" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="inapp_native"
            :label="$t('ProjectSettings_Field_Feature_InappNative')"
            :description="$t('ProjectSettings_Field_Feature_InappNative_Desc')"
          >
            <feature-toggle v-model="value.inapp_native" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="ai_text_generation"
            :label="$t('ProjectSettings_Field_Ai')"
            :description="$t('ProjectSettings_Field_Ai_Desc')"
          >
            <feature-toggle v-model="value.ai_text_generation" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="campaign_templates"
            :label="$t('ProjectSettings_Field_CampaignTemplates')"
            :description="$t('ProjectSettings_Field_CampaignTemplates_Desc')"
          >
            <feature-toggle v-model="value.campaign_templates" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="betsson_integration"
            :label="$t('ProjectSettings_Field_Betsson_Integration')"
            :description="$t('ProjectSettings_Field_Betsson_Integration_Desc')"
          >
            <feature-toggle v-model="value.betsson_integration" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="feature_campaign_throttling"
            :label="$t('ProjectSettings_Field_Feature_CampaignThrottling')"
            :description="
              $t('ProjectSettings_Field_Feature_CampaignThrottling_Desc')
            "
          >
            <feature-toggle v-model="value.feature_campaign_throttling" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="allow_broadcast_for_multistage_timebased_campaigns"
            :label="
              $t(
                'ProjectSettings_Field_Feature_AllowBroadcastForMultistageTimebasedCampaigns'
              )
            "
            :description="
              $t(
                'ProjectSettings_Field_Feature_AllowBroadcastForMultistageTimebasedCampaigns'
              )
            "
          >
            <toggle
              v-model="value.allow_broadcast_for_multistage_timebased_campaigns"
              :active-text="$t('ProjectSettings_Toggle_Allowed')"
              :inactive-text="$t('ProjectSettings_Toggle_Disallowed')"
            />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="show_open_bet_integration"
            :label="$t('ProjectSettings_Field_ShowOpenBetIntegration')"
            :description="
              $t('ProjectSettings_Field_ShowOpenBetIntegration_Desc')
            "
          >
            <feature-toggle v-model="value.show_open_bet_integration" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="automation_campaign_execution"
            :label="$t('ProjectSettings_Field_Profile_Import_Campaign_Trigger')"
            :description="
              $t('ProjectSettings_Field_Profile_Import_Campaign_Trigger_Desc')
            "
          >
            <feature-toggle v-model="value.automation_campaign_execution" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="show_inbox_open_stats"
            :label="$t('ProjectSettings_Field_ShowInboxOpenStats')"
            :description="$t('ProjectSettings_Field_ShowInboxOpenStats_Desc')"
          >
            <feature-toggle v-model="value.show_inbox_open_stats" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="igt_integration"
            :label="$t('ProjectSettings_Field_Igt_Integration')"
            :description="$t('ProjectSettings_Field_Igt_Integration_Desc')"
          >
            <el-tooltip
              effect="light"
              :disabled="!!value.subscription_preferences"
              :content="$t('ProjectSettings_Tooltip_IGT_Requirements')"
              placement="right"
            >
              <feature-toggle
                :disabled="!value.subscription_preferences"
                v-model="value.igt_integration"
              />
            </el-tooltip>
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="opap_webhook_integration"
            :label="$t('ProjectSettings_Field_OPAPWebhookIntegration')"
            :description="
              $t('ProjectSettings_Field_OPAPWebhookIntegration_Desc')
            "
          >
            <feature-toggle v-model="value.opap_webhook_integration" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="allow_authorised_devices"
            :label="$t('ProjectSettings_Field_AllowAuthorisedDevices')"
            :description="
              $t('ProjectSettings_Field_AllowAuthorisedDevices_Desc')
            "
          >
            <feature-toggle v-model="value.allow_authorised_devices" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="workflow_clickmap"
            :label="$t('ProjectSettings_Field_WorkflowClickmap')"
            :description="$t('ProjectSettings_Field_WorkflowClickmap_Desc')"
          >
            <feature-toggle v-model="value.workflow_clickmap" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="save_as_report_campaign_start_time"
            :label="$t('ProjectSettings_Field_SaveAsReportCampaignStartTime')"
            :description="
              $t('ProjectSettings_Field_SaveAsReportCampaignStartTime_Desc')
            "
          >
            <feature-toggle
              v-model="value.save_as_report_campaign_start_time"
            />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="save_as_report_delta"
            :label="$t('ProjectSettings_Field_SaveAsReportDelta')"
            :description="$t('ProjectSettings_Field_SaveAsReportDelta_Desc')"
          >
            <feature-toggle v-model="value.save_as_report_delta" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="allow_voucher_codes"
            :label="$t('ProjectSettings_Field_VoucherCode')"
            :description="$t('ProjectSettings_Field_VoucherCode_Desc')"
          >
            <feature-toggle v-model="value.allow_voucher_codes" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="oasis_integration"
            :label="$t('ProjectSettings_Field_Oasis_Integration')"
            :description="$t('ProjectSettings_Field_Oasis_Integration_Desc')"
          >
            <feature-toggle v-model="value.oasis_integration" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="campaign_content_preview"
            :label="$t('ProjectSettings_Field_Campaign_Content_Preview')"
            :description="
              $t('ProjectSettings_Field_Campaign_Content_Preview_Desc')
            "
          >
            <feature-toggle v-model="value.campaign_content_preview" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="advanced_profile_search"
            :label="$t('ProjectSettings_Field_Advanced_Profile_Search')"
            :description="
              $t('ProjectSettings_Field_Advanced_Profile_Search_Desc')
            "
          >
            <feature-toggle v-model="value.advanced_profile_search" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="optimove_lists"
            :label="$t('ProjectSettings_Field_Optimove_Lists')"
            :description="$t('ProjectSettings_Field_Optimove_Lists_Desc')"
          >
            <feature-toggle v-model="value.optimove_lists" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="action_aggregation_enabled"
            :label="$t('ProjectSettings_Field_Action_Message_Aggregation')"
            :description="
              $t('ProjectSettings_Field_Action_Message_Aggregation_Desc')
            "
          >
            <feature-toggle v-model="value.action_aggregation_enabled" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="no_changed_sub_tracking"
            :label="$t('ProjectSettings_Field_NoChanged_Subs')"
            :description="$t('ProjectSettings_Field_NoChanged_Subs_Desc')"
          >
            <feature-toggle v-model="value.no_changed_sub_tracking" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="no_changed_attribute_handling"
            :label="$t('ProjectSettings_Field_NoChanged_Attribute_Handling')"
            :description="
              $t('ProjectSettings_Field_NoChanged_Attribute_Handling_Desc')
            "
          >
            <feature-toggle v-model="value.no_changed_attribute_handling" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="gateway_cost_tracking"
            :label="$t('ProjectSettings_Field_Feature_GatewayCostTracking')"
            :description="
              $t('ProjectSettings_Field_Feature_GatewayCostTracking_Desc')
            "
          >
            <feature-toggle v-model="value.gateway_cost_tracking" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="enable_new_goal_conversion"
            :label="$t('ProjectSettings_Field_EnableNewGoalConversion')"
            :description="
              $t('ProjectSettings_Field_EnableNewGoalConversion_Desc')
            "
          >
            <feature-toggle v-model="value.enable_new_goal_conversion" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="enable_campaign_comparison_conversions"
            :label="
              $t('ProjectSettings_Field_EnableCampaignComparisonConversions')
            "
            :description="
              $t(
                'ProjectSettings_Field_EnableCampaignComparisonConversions_Desc'
              )
            "
          >
            <feature-toggle
              v-model="value.enable_campaign_comparison_conversions"
            />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="queue_email_msg_id_only"
            :label="$t('ProjectSettings_Field_QueueEmailMsgIdOnly')"
            :description="$t('ProjectSettings_Field_QueueEmailMsgIdOnly_Desc')"
          >
            <feature-toggle v-model="value.queue_email_msg_id_only" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="enable_email_last_open"
            :label="$t('ProjectSettings_Field_SegmentEmailLastOpened')"
            :description="
              $t('ProjectSettings_Field_SegmentEmailLastOpened_Desc')
            "
          >
            <feature-toggle v-model="value.enable_email_last_open" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="preaggregated_clicked_links"
            :label="
              $t('ProjectSettings_Field_Feature_PreaggregatedClickedLinks')
            "
            :description="
              $t('ProjectSettings_Field_Feature_PreaggregatedClickedLinks_Desc')
            "
          >
            <feature-toggle v-model="value.preaggregated_clicked_links" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="mxa_campaign_options"
            :label="$t('ProjectSettings_Field_Feature_MxaCampaignOptions')"
            :description="
              $t('ProjectSettings_Field_Feature_MxaCampaignOptions_Desc')
            "
          >
            <feature-toggle v-model="value.mxa_campaign_options" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="allow_five_character_language_code"
            :label="
              $t('ProjectSettings_Field_Feature_AllowFiveCharacterLanguageCode')
            "
            :description="
              $t(
                'ProjectSettings_Field_Feature_AllowFiveCharacterLanguageCode_Desc'
              )
            "
          >
            <feature-toggle
              v-model="value.allow_five_character_language_code"
            />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="ios_live_activities"
            :label="$t('ProjectSettings_Field_Ios_Live_Activities')"
            :description="$t('ProjectSettings_Field_Ios_Live_Activities_Desc')"
          >
            <feature-toggle v-model="value.ios_live_activities" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="new_mxa"
            :label="$t('ProjectSettings_Field_New_Mxa')"
            :description="$t('ProjectSettings_Field_New_Mxa_Desc')"
          >
            <feature-toggle
              :disabled="newMxaDisabled"
              v-model="value.new_mxa"
            />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="campaign_trigger_by_consent_pref_events"
            :label="
              $t('ProjectSettings_Field_CampaignTriggerByConsentPrefEvents')
            "
            :description="
              $t(
                'ProjectSettings_Field_CampaignTriggerByConsentPrefEvents_Desc'
              )
            "
          >
            <feature-toggle
              v-model="value.campaign_trigger_by_consent_pref_events"
            />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="rum_tracking"
            :label="$t('ProjectSettings_Field_Rum_Tracking')"
            :description="$t('ProjectSettings_Field_Rum_Tracking_Desc')"
          >
            <feature-toggle v-model="value.rum_tracking" />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="mxa_migration_tool"
            :label="$t('ProjectSettings_Field_Mxa_Migration_Tool')"
            :description="$t('ProjectSettings_Field_Mxa_Migration_Tool_Desc')"
          >
            <feature-toggle
              v-model="value.mxa_migration_tool"
              :disabled="!value.new_mxa"
            />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="mxa_event_node"
            :label="$t('ProjectSettings_Field_Mxa_Event_Node')"
            :description="$t('ProjectSettings_Field_Mxa_Event_Node_Desc')"
          >
            <feature-toggle
              v-model="value.mxa_event_node"
              :disabled="!value.new_mxa"
            />
          </form-item>
        </el-col>
        <el-col :span="12">
          <form-item
            prop="warply_integration"
            :label="$t('ProjectSettings_Field_ShowWarplyIntegration')"
            :description="
              $t('ProjectSettings_Field_ShowWarplyIntegration_Desc')
            "
          >
            <feature-toggle v-model="value.warply_integration" />
          </form-item>
        </el-col>
      </el-row>
    </el-form>
  </form-section>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import { $isSuperAdmin } from '@/core/utils/VueAcl'
import { mapGetters } from 'vuex'
import FeatureToggle from '@/common/components/form/FeatureToggle'
import Toggle from '@/common/components/form/Toggle'

export default {
  name: 'FeatureFlagsSection',
  components: { Toggle, FeatureToggle, FormItem, FormSection },
  props: {
    value: {
      type: Object,
      required: true
    },
    features: {
      type: Object,
      required: true
    },
    changedFeatures: {
      type: Array,
      required: true
    }
  },
  computed: {
    ...mapGetters(['mxaDomain']),
    workflowActivityAuditLink() {
      return `https://${this.mxaDomain}/project/${this.value.id}/admin/log`
    },
    checkAuditLogLink() {
      return !!this.value.maxautomation && $isSuperAdmin()
    },
    shouldShowSportradarWarning() {
      return this.changedFeatures.find(
        feature => feature.name === 'sportradar' && feature.status === 0
      )
    },
    isEmailSettingsVisible() {
      return this.$can('XpEmailConfiguration:ReadAccess')
    },
    isEmailSettingsDisabled() {
      return !this.$can('XpEmailConfiguration:FullAccess')
    }
  },
  watch: {
    'value.new_mxa'(value) {
      if (!value) {
        this.value.mxa_migration_tool = 0
      }
    }
  },

  data() {
    return {
      newMxaDisabled: this.value.new_mxa
    }
  }
}
</script>

<style scoped>
.flexbox {
  display: flex;
  align-items: flex-end;
}

small {
  color: #7e8596;
}
</style>



// WEBPACK FOOTER //
// src/modules/project/components/FeatureFlagsSection.vue